import React from "react";
import { motion } from "framer-motion";
import { Instagram, Twitter, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { QDMonogram } from "./Header";

const FooterCard = React.forwardRef(function FooterCard(
  { settings, depth = 0, onReset, dragHandlers, style, animate, initial, exit, transition },
  ref
) {
  const s = settings || {};
  return (
    <motion.section
      ref={ref}
      data-testid="footer-card"
      drag={depth === 0 ? "y" : false}
      dragElastic={0.35}
      dragMomentum={false}
      {...(dragHandlers || {})}
      initial={initial}
      animate={animate}
      exit={exit}
      transition={transition}
      style={style}
      className="absolute inset-0 bg-[#FDFBF7] text-[#1A1A1A] overflow-y-auto"
    >
      <div className="min-h-full flex flex-col px-6 sm:px-12 lg:px-20 pt-28 pb-12">
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <QDMonogram size={64} />
              <div>
                <p className="font-serif-display text-3xl tracking-[0.18em] uppercase">Queens</p>
                <p className="font-serif-display italic text-2xl tracking-[0.18em] uppercase text-[#76746F]">Daily</p>
              </div>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Stories for the
              <br />
              <span className="italic font-light">brave.</span>
            </h2>
            <p className="mt-6 font-sans-modern text-base text-[#1A1A1A]/75 leading-relaxed max-w-md">
              {s.about || "An editorial home for stories that matter — shaped by and for women."}
            </p>

            <button
              type="button"
              data-testid="footer-back-to-top"
              onClick={onReset}
              className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-full border border-black/20 hover:bg-black hover:text-white hover:border-black transition-colors text-sm"
            >
              Back to the top
            </button>
          </div>

          {/* Links */}
          <div className="md:col-span-3">
            <p className="overline text-[#76746F] mb-4">The Magazine</p>
            <ul className="space-y-2.5 font-sans-modern text-base">
              <li>
                <Link to="/about" data-testid="footer-about" className="hover:underline underline-offset-4">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/advertise" data-testid="footer-advertise" className="hover:underline underline-offset-4">
                  Advertise with Us
                </Link>
              </li>
              <li>
                <Link to="/contact" data-testid="footer-contact" className="hover:underline underline-offset-4">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/admin/login" data-testid="footer-admin" className="text-sm text-[#76746F] hover:text-[#1A1A1A] hover:underline underline-offset-4">
                  Admin Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <p className="overline text-[#76746F] mb-4">Get in touch</p>
            <ul className="space-y-3 font-sans-modern text-sm">
              {s.contact_email && (
                <li className="flex items-center gap-2.5">
                  <Mail size={14} strokeWidth={1.5} />
                  <a href={`mailto:${s.contact_email}`} className="hover:underline underline-offset-4">
                    {s.contact_email}
                  </a>
                </li>
              )}
              {s.contact_phone && (
                <li className="flex items-center gap-2.5">
                  <Phone size={14} strokeWidth={1.5} />
                  <span>{s.contact_phone}</span>
                </li>
              )}
              {s.address && (
                <li className="flex items-start gap-2.5">
                  <MapPin size={14} strokeWidth={1.5} className="mt-0.5" />
                  <span>{s.address}</span>
                </li>
              )}
            </ul>

            <p className="overline text-[#76746F] mt-8 mb-4">Follow</p>
            <div className="flex items-center gap-3">
              {s.instagram && (
                <a href={s.instagram} target="_blank" rel="noreferrer"
                   data-testid="social-instagram"
                   className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-colors">
                  <Instagram size={16} strokeWidth={1.5} />
                </a>
              )}
              {s.twitter && (
                <a href={s.twitter} target="_blank" rel="noreferrer"
                   data-testid="social-twitter"
                   className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-colors">
                  <Twitter size={16} strokeWidth={1.5} />
                </a>
              )}
              {s.facebook && (
                <a href={s.facebook} target="_blank" rel="noreferrer"
                   data-testid="social-facebook"
                   className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-colors">
                  <Facebook size={16} strokeWidth={1.5} />
                </a>
              )}
              {s.youtube && (
                <a href={s.youtube} target="_blank" rel="noreferrer"
                   data-testid="social-youtube"
                   className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-colors">
                  <Youtube size={16} strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-black/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#76746F]">
          <span>© {new Date().getFullYear()} Queens Daily. All rights reserved.</span>
          <span className="overline">The end · Swipe down to return</span>
        </div>
      </div>
    </motion.section>
  );
});

export default FooterCard;
